import {
  AloftPrice,
  orUndefined,
  schAlertType,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getCompTypeLabel, getScopedAlertType } from '../utils/compTypeScoping.ts';
import {
  OVERLY_ADJUSTED_REFERENCE_TIPS,
  getOverlyAdjustedTargetCellKey,
  getOverlyAdjustedTargetCellTooltip,
  isOverlyAdjusted,
} from './adjustmentThresholdUtils.ts';

export const overlyAdjustedComparable: AlertDefinition = ({
  compAdjustments,
  compSequence,
  fullCompSequence,
  compType,
  propertyDataEntities,
  order,
}) => {
  const faultyComps = compSequence
    .map((id) => {
      const propertyDataState = propertyDataEntities[id] as CompPropertyDataState | undefined;
      const salePrice = orUndefined(AloftPrice.schema.parse)(
        propertyDataState?.compData?.editableData?.transaction?.price,
      );

      return {
        id,
        isOverlyAdjusted: isOverlyAdjusted(compAdjustments[id], salePrice),
      };
    })
    .filter(({ isOverlyAdjusted: overlyAdjusted }) => overlyAdjusted);

  if (faultyComps.length === 0) return [];

  const fixRoute =
    schPairedCostRouteItem.enum[order.isInspectifyOrder ? 'adjustments-grid' : 'adjustments'];
  const triggers = getAlertTriggers({ fixRoute, order });

  return faultyComps.map(({ id }) => {
    const compNumber = (fullCompSequence ?? compSequence).indexOf(id) + 1;
    let comparableLabel = `Comparable ${compNumber}`;
    switch (compType) {
      case 'aiv':
      case 'arv':
        comparableLabel = `${getCompTypeLabel(compType)} Comparable ${compNumber}`;
        break;
      default:
        break;
    }

    const compAdjustment = compAdjustments[id];
    const propertyDataState = propertyDataEntities[id] as CompPropertyDataState | undefined;
    const salePrice = orUndefined(AloftPrice.schema.parse)(
      propertyDataState?.compData?.editableData?.transaction?.price,
    );
    const targetCellKey = getOverlyAdjustedTargetCellKey(id, compAdjustment, salePrice);
    const targetCellTooltip = getOverlyAdjustedTargetCellTooltip(targetCellKey);

    return {
      type: getScopedAlertType(schAlertType.enum.overlyAdjustedComparable, compType),
      status: 'active' as const,
      severity: 'error' as const,
      orderType: ['saas', 'appraisify'] as const,
      message: `${comparableLabel} is adjusted beyond the recommended threshold.`,
      propertyID: id,
      ...(compType ? { compType } : {}),
      fixingInfo: {
        tips: OVERLY_ADJUSTED_REFERENCE_TIPS,
        pairedCostRouteItem: fixRoute,
        fixingMethod: ['comment', 'action'] as const,
        commentAlwaysRequired: false,
        ...(targetCellKey ? { targetCellKey } : {}),
        ...(targetCellTooltip ? { targetCellTooltip } : {}),
      },
      triggers,
    };
  });
};
